import React from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'

export type RedirectTarget =
  | string
  | ((ctx: { params: Record<string, string | undefined>; search: URLSearchParams }) => string)

const pickTab = (search: URLSearchParams, valid: string[], fallback: string) => {
  const t = search.get('tab')
  return t && valid.includes(t) ? t : fallback
}

const FEATURES_FROM_SISTEMA: Record<string, string> = {
  tracking: 'tracking',
  averbacao: 'averbacao',
  'criar-processos': 'criar-processos',
  'numerario-dev': 'numerario',
  legislacao: 'legislacao',
  atividades: 'atividades',
}

const JOBS_TAB: Record<string, string> = {
  microsservicos: 'servicos',
  diagnostico: 'servicos',
  fila: 'jobs',
  historico: 'historico',
}

/** Rotas antigas → endereço único no admin. Remover na rodada seguinte à que entrar em PROD. */
export const LEGACY_ADMIN_REDIRECTS: ReadonlyArray<{ from: string; to: RedirectTarget }> = [
  { from: '/automacoes/gerenciar', to: '/admin/automacoes/gerenciar' },
  { from: '/automacoes/painel', to: '/admin/automacoes/execucoes' },
  { from: '/automacoes/painel/:id', to: ({ params }) => `/admin/automacoes/execucoes/${params.id}` },
  { from: '/automacoes/status', to: '/admin/automacoes/gerenciar' },
  { from: '/admin/automacoes/status', to: '/admin/automacoes/gerenciar' },
  {
    from: '/automacoes/editor-campos/:id?',
    to: ({ params }) => (params.id ? `/admin/automacoes/campos/${params.id}` : '/admin/automacoes/campos'),
  },
  { from: '/jobs', to: '/admin/microsservicos/servicos' },
  { from: '/admin/microsservicos/diagnostico', to: '/admin/microsservicos/servicos' },
  { from: '/access-logs', to: '/admin/monitoramento/logs' },
  { from: '/tracking/auto-config', to: '/admin/features/tracking' },
  { from: '/process-types', to: '/admin/tipos-processo' },
  {
    from: '/admin/sistema/automacoes',
    to: ({ search }) =>
      `/admin/automacoes/${pickTab(search, ['catalogo', 'gerenciar', 'execucoes'], 'gerenciar')}`,
  },
  { from: '/admin/sistema/sumario-automations', to: '/admin/automacoes/catalogo' },
  {
    from: '/admin/sistema/jobs',
    to: ({ search }) => `/admin/microsservicos/${JOBS_TAB[search.get('tab') ?? ''] ?? 'servicos'}`,
  },
  { from: '/admin/sistema/logs', to: '/admin/monitoramento/logs' },
  { from: '/admin/sistema/fluxos', to: '/admin/monitoramento/fluxos' },
  {
    from: '/admin/sistema/averbacao',
    to: ({ search }) =>
      search.get('tab') === 'empresas' ? '/admin/features/averbacao?view=empresas' : '/admin/features/averbacao',
  },
  { from: '/admin/sistema/averbacao/terminais/:id', to: ({ params }) => `/admin/features/averbacao/terminais/${params.id}` },
  ...Object.entries(FEATURES_FROM_SISTEMA)
    .filter(([old]) => old !== 'averbacao')
    .map(([old, tab]) => ({ from: `/admin/sistema/${old}`, to: `/admin/features/${tab}` })),
  { from: '/admin/sistema/veloster', to: '/admin/veloster' },
  { from: '/admin/sistema/docs', to: '/admin/docs' },
  { from: '/admin/companies', to: '/admin/empresas' },
  { from: '/admin/companies/:id', to: ({ params }) => `/admin/empresas/${params.id}` },
  { from: '/admin/users', to: '/admin/usuarios' },
  { from: '/admin/agents', to: '/admin/agentes' },
  { from: '/admin/process-types', to: '/admin/tipos-processo' },
  { from: '/admin/process-types/:id', to: ({ params }) => `/admin/tipos-processo/${params.id}` },
  { from: '/admin/feature-gate', to: '/admin/empresas' },
  { from: '/admin/feature-gate/:companyId', to: ({ params }) => `/admin/empresas/${params.companyId}?section=features` },
]

export function legacyRedirect(to: RedirectTarget): React.FC {
  const Redirect: React.FC = () => {
    const params = useParams()
    const location = useLocation()
    const search = new URLSearchParams(location.search)
    const target = typeof to === 'function' ? to({ params, search }) : to
    search.delete('tab')
    const extra = search.toString()
    if (!extra) return <Navigate to={target} replace />
    const sep = target.includes('?') ? '&' : '?'
    return <Navigate to={`${target}${sep}${extra}`} replace />
  }
  return Redirect
}
